import { GlobalDayWithInterval, GlobalInterval, GlobalTimeslots, OpeningModeEnum } from '@api/ringil-api.v2.tsx';
import { AdditionalPerson, ExpeditionLocality, LocalityEntryModeEnum, ManualAddress, NewExpeditionForm } from '@app/pages/expedition/create/types.ts';
import { validateRouteAssignments } from '@app/pages/expedition/create/routeRules.ts';

/**
 * Deciding which stops of the merged shipments are really the same stop.
 *
 * Two stops only collapse into one when they are the same place, when there is a window that suits
 * both, and — decisively — when collapsing them does not put any order out of order. That last check
 * is what keeps A→B merged with B→A intact: folding the two A stops together would make the second
 * shipment's order unload before it is loaded, so the stops stay apart.
 */

/**
 * Identifies a place: the address-book entry when there is one, otherwise the typed-in address.
 *
 * The two kinds never meet — a stop entered by hand and a stop picked from the address book stay
 * separate even when they spell out the same address, because only the user can say they are the
 * same place. That is why the mode decides which key is built, rather than whichever field happens
 * to be filled in.
 *
 * A typed-in address only counts as the same place when the whole address matches — name, street,
 * city, postcode and country alike. Nothing identifies it beyond what was typed, so a partial match
 * is not enough to claim two stops are one, and an address missing any of the five parts is never
 * merged at all (null). Contact details are deliberately left out: they say who to call at the
 * place, not which place it is.
 */
export const locationKey = (loc: ExpeditionLocality): string | null => {
  if (loc.localityMode !== LocalityEntryModeEnum.MANUALLY) {
    const locationId = loc.companyLocationId || loc.location?.locationId;
    return locationId ? `id:${locationId}` : null;
  }

  const address = loc.manualAddress;
  const parts = [address?.name, address?.streetNr, address?.city, address?.postalCode, address?.countryCode3].map(part => (part || '').trim().toLowerCase());

  return parts.every(part => part.length > 0) ? `addr:${parts.join('|')}` : null;
};

const toMinutes = (time?: string): number | null => {
  const [hours, minutes] = (time || '').split(':');
  const parsed = Number(hours) * 60 + Number(minutes);
  return Number.isFinite(parsed) ? parsed : null;
};

const toTime = (minutes: number): string => `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

const DAY_MINUTES = 24 * 60;

/** A stretch of the day in minutes from midnight, as [since, till]. */
type Segment = [number, number];

/**
 * The minutes an interval covers.
 *
 * A window that runs past midnight — 22:00–06:00 for a night loading slot — is one window to the
 * yard but two stretches of a clock that resets at midnight, so it is cut in two here and put back
 * together in {@link joinAcrossMidnight} once the overlap is known.
 */
const toSegments = (interval: GlobalInterval): Segment[] => {
  const since = toMinutes(interval.sinceHoursString) ?? 0;
  const till = toMinutes(interval.tillHoursString) ?? DAY_MINUTES;

  return till < since
    ? [
        [since, DAY_MINUTES],
        [0, till],
      ]
    : [[since, till]];
};

/** Rejoins a window that was cut at midnight, so 22:00–24:00 with 00:00–06:00 reads as 22:00–06:00 again. */
const joinAcrossMidnight = (segments: Segment[]): Segment[] => {
  const beforeMidnight = segments.find(([, till]) => till === DAY_MINUTES);
  const afterMidnight = segments.find(([since]) => since === 0);
  if (!beforeMidnight || !afterMidnight || beforeMidnight === afterMidnight) return segments;

  return [...segments.filter(segment => segment !== beforeMidnight && segment !== afterMidnight), [beforeMidnight[0], afterMidnight[1]]];
};

/** The hours both stops can be served in, or null when their windows never coincide. */
const intersectIntervals = (left: GlobalInterval[], right: GlobalInterval[]): GlobalInterval[] | null => {
  // An empty list means "no restriction", so the other side's window stands unchanged.
  if (!left?.length) return right ?? [];
  if (!right?.length) return left;

  const rightSegments = right.flatMap(toSegments);
  const overlaps = new Map<string, Segment>();

  left.flatMap(toSegments).forEach(a => {
    rightSegments.forEach(b => {
      const since = Math.max(a[0], b[0]);
      const till = Math.min(a[1], b[1]);

      // Windows that only touch do not overlap: 08:00–10:00 and 10:00–12:00 share no time at all.
      // A window that *is* a single moment is the exception — it has nothing but its edges, and a
      // stop asking for 08:00 sharp still fits inside 08:00–10:00.
      const isMoment = a[0] === a[1] || b[0] === b[1];
      if (since < till || (since === till && isMoment)) overlaps.set(`${since}-${till}`, [since, till]);
    });
  });

  if (overlaps.size === 0) return null;
  return joinAcrossMidnight(Array.from(overlaps.values())).map(([since, till]) => ({ sinceHoursString: toTime(since), tillHoursString: toTime(till) }));
};

export const dayKey = (day: GlobalDayWithInterval): string => (day.utcDay || day.day || '').substring(0, 10);

/**
 * The widest window that still suits both stops: the days they have in common, and on each of those
 * the hours they have in common.
 *
 * Only the overlap can be promised to both shipments — 1.–10. 8. together with 5.–15. 8. leaves
 * 5.–10. 8. Where there is no overlap at all the answer is null and the stops stay separate, since
 * one visit cannot serve both. A stop with no days set imposes nothing and takes the other's window.
 */
export const intersectTimeslots = (left: GlobalTimeslots, right: GlobalTimeslots): GlobalTimeslots | null => {
  const leftDays = left?.dayWithInterval ?? [];
  const rightDays = right?.dayWithInterval ?? [];

  if (leftDays.length === 0) return right ?? left ?? { dayWithInterval: [] };
  if (rightDays.length === 0) return left;

  const rightByDay = new Map(rightDays.map(day => [dayKey(day), day]));
  const shared: GlobalDayWithInterval[] = [];

  leftDays.forEach(day => {
    const counterpart = rightByDay.get(dayKey(day));
    if (!counterpart) return;

    const dayClosed = day.openingMode === OpeningModeEnum.CLOSED;
    const counterpartClosed = counterpart.openingMode === OpeningModeEnum.CLOSED;

    // Both closed is not a disagreement: a stop typed in by hand starts out closed with no hours at
    // all, so rejecting that pair would mean two identical manual stops could never merge.
    if (dayClosed && counterpartClosed) {
      shared.push({ ...day, intervals: [] });
      return;
    }

    // A day one side has closed cannot be served, whatever the other side's hours say.
    if (dayClosed || counterpartClosed) return;

    const intervals = intersectIntervals(day.intervals, counterpart.intervals);
    if (intervals === null) return;

    shared.push({ ...day, intervals });
  });

  if (shared.length === 0) return null;
  return { ...left, dayWithInterval: shared };
};

/**
 * When a stop is first wanted, as a sortable number, or null when no date is set.
 *
 * Combines the earliest day with the earliest hour on it, so two stops on the same day still order
 * by their time window.
 */
export const stopStartsAt = (loc: ExpeditionLocality): number | null => {
  const days = loc.timeslots?.dayWithInterval ?? [];
  if (days.length === 0) return null;

  const starts = days
    .map(day => {
      const date = Date.parse(`${dayKey(day)}T00:00:00Z`);
      if (Number.isNaN(date)) return null;
      const earliestInterval = (day.intervals ?? []).map(interval => toMinutes(interval.sinceHoursString) ?? 0).sort((a, b) => a - b)[0] ?? 0;
      return date + earliestInterval * 60_000;
    })
    .filter((value): value is number => value !== null);

  return starts.length > 0 ? Math.min(...starts) : null;
};

/**
 * Orders stops by when they are wanted, keeping the given order for ties and for stops with no date
 * yet — those go last, since there is nothing to place them by.
 */
export const sortStopsByDate = (locations: ExpeditionLocality[]): ExpeditionLocality[] =>
  locations
    .map((loc, index) => ({ loc, index, startsAt: stopStartsAt(loc) }))
    .sort((a, b) => {
      if (a.startsAt === b.startsAt) return a.index - b.index;
      if (a.startsAt === null) return 1;
      if (b.startsAt === null) return -1;
      return a.startsAt - b.startsAt;
    })
    .map(entry => entry.loc);

export type LocationContactField = 'contactPerson' | 'contactPhone' | 'contactEmail';

/** A contact detail the two stops disagreed on; it was left empty for the user to decide. */
export interface LocationContactConflict {
  locationName: string;
  field: LocationContactField;
  /** The distinct values the stops held, ready to show. */
  values: string[];
}

/**
 * The shared contact detail, or nothing plus a conflict to report.
 *
 * Same rule as the shipment-level fields in {@link mergeExpeditionsToForm}: a stop that simply has
 * no phone number is not disagreeing with one that has, but two different numbers are — and picking
 * one of them for the user would quietly send the driver to the wrong person.
 */
const resolveContact = (values: (string | undefined)[], field: LocationContactField, locationName: string, conflicts: LocationContactConflict[]): string => {
  const stated = values.filter(value => (value || '').trim().length > 0);
  if (stated.length === 0) return undefined;

  const distinct = new Map(stated.map(value => [value.trim().toLowerCase(), value]));
  if (distinct.size === 1) return stated[0];

  conflicts.push({ locationName, field, values: Array.from(distinct.values()) });
  return undefined;
};

const unionPersons = (...lists: AdditionalPerson[][]): AdditionalPerson[] => {
  const byEmail = new Map((lists.flat().filter(person => (person?.email || '').trim().length > 0) || []).map(person => [person.email.trim().toLowerCase(), person]));
  return Array.from(byEmail.values());
};

/** Folds the typed-in contact details of two stops at the same address into one. */
const combineManualAddresses = (target: ManualAddress, source: ManualAddress, locationName: string, conflicts: LocationContactConflict[]): ManualAddress => {
  // The address itself is identical — that is what made the stops one place — so only the people
  // need reconciling: single fields the way any other field is merged, the extra people unioned.
  // Resolved in the order they are shown, so the reported conflicts read as the form does.
  const mainContactPerson = resolveContact([target?.mainContactPerson, source?.mainContactPerson], 'contactPerson', locationName, conflicts);
  const mainContactPhone = resolveContact([target?.mainContactPhone, source?.mainContactPhone], 'contactPhone', locationName, conflicts);
  const mainContactEmail = resolveContact([target?.mainContactEmail, source?.mainContactEmail], 'contactEmail', locationName, conflicts);

  return {
    ...target,
    mainContactPerson,
    mainContactPhone,
    mainContactEmail,
    // Nobody left to notify once the address is gone; keeping the flag would only look like a promise.
    notifySelectedPerson: Boolean(mainContactEmail) && Boolean(target?.notifySelectedPerson || source?.notifySelectedPerson),
    additionalPersons: unionPersons(target?.additionalPersons || [], source?.additionalPersons || []),
  };
};

/** Folds `source` into `target`, keeping everything both of them carried. */
export const combineStops = (target: ExpeditionLocality, source: ExpeditionLocality, timeslots: GlobalTimeslots, conflicts: LocationContactConflict[]): ExpeditionLocality => {
  const contactsByEmail = new Map(
    [...(target.additionalContacts || []), ...(source.additionalContacts || [])].filter(contact => contact?.contactEmail).map(c => [c.contactEmail, c]),
  );

  const isManual = target.localityMode === LocalityEntryModeEnum.MANUALLY;
  const locationName = stopName(target);

  // An address-book stop keeps its contact in `selectedPerson`, a typed-in one inside the address;
  // both are resolved the same way, so whichever the stop uses, a disagreement is reported and left
  // for the user rather than decided here.
  const selectedPersonName = isManual
    ? undefined
    : resolveContact(
        [target.selectedPerson, source.selectedPerson].map(person => (person ? person.contactName || person.contactEmail : undefined)),
        'contactPerson',
        locationName,
        conflicts,
      );

  return {
    ...target,
    timeslots,
    orders: [...(target.orders || []), ...(source.orders || [])],
    unloadingOrderIds:
      target.unloadingOrderIds || source.unloadingOrderIds ? Array.from(new Set([...(target.unloadingOrderIds || []), ...(source.unloadingOrderIds || [])])) : null,
    selectedPerson: isManual
      ? target.selectedPerson
      : [target.selectedPerson, source.selectedPerson].find(person => (person?.contactName || person?.contactEmail) === selectedPersonName) || undefined,
    manualAddress: isManual ? combineManualAddresses(target.manualAddress, source.manualAddress, locationName, conflicts) : target.manualAddress,
    additionalContacts: contactsByEmail.size > 0 ? Array.from(contactsByEmail.values()) : target.additionalContacts,
    additionalPersons: unionPersons(target.additionalPersons || [], source.additionalPersons || []),
    loadingReference:
      [target.loadingReference, source.loadingReference]
        .map(reference => (reference || '').trim())
        .filter(reference => reference.length > 0)
        .join(', ') || undefined,
  };
};

export interface MergedRoute {
  locations: ExpeditionLocality[];
  /** Stops that stayed separate although they are the same place, and why. */
  keptApart: { locationName: string; reason: 'timeslots' | 'wouldInvalidateOrders' }[];
  /** Contact details that differed between stops that were folded into one. */
  contactConflicts: LocationContactConflict[];
  /**
   * Stops folded although their windows never overlap, keeping the first one's window. Only a route
   * whose stops are fixed in advance (route optimization) does this; the date merge keeps such stops apart.
   */
  timeslotConflicts?: { locationName: string }[];
}

export const stopName = (loc: ExpeditionLocality): string => loc.location?.locationName || loc.manualAddress?.name || '';

/**
 * Collapses repeated stops in an already-ordered route.
 *
 * Works pair by pair, earliest first, and keeps a merge only when the route still passes
 * {@link validateRouteAssignments} with no more problems than before. Merging is a convenience; a
 * route the user then has to repair by hand is worse than one extra stop.
 */
export const mergeRepeatedStops = (values: NewExpeditionForm, locations: ExpeditionLocality[]): MergedRoute => {
  const issueCount = (candidate: ExpeditionLocality[]) => validateRouteAssignments({ ...values, locations: candidate }).length;

  let current = [...locations];
  const keptApart: MergedRoute['keptApart'] = [];
  const contactConflicts: LocationContactConflict[] = [];
  let merged = true;

  while (merged) {
    merged = false;

    outer: for (let i = 0; i < current.length; i++) {
      const keyI = locationKey(current[i]);
      if (!keyI) continue;

      for (let j = i + 1; j < current.length; j++) {
        if (locationKey(current[j]) !== keyI) continue;

        const timeslots = intersectTimeslots(current[i].timeslots, current[j].timeslots);
        if (timeslots === null) {
          keptApart.push({ locationName: stopName(current[i]), reason: 'timeslots' });
          continue;
        }

        // Collected aside: a merge that is rolled back below must not leave its conflicts behind.
        const pairConflicts: LocationContactConflict[] = [];
        const candidate = current.filter((_, index) => index !== j);
        candidate[i] = combineStops(current[i], current[j], timeslots, pairConflicts);

        if (issueCount(candidate) > issueCount(current)) {
          keptApart.push({ locationName: stopName(current[i]), reason: 'wouldInvalidateOrders' });
          continue;
        }

        contactConflicts.push(...pairConflicts);
        current = candidate;
        merged = true;
        break outer;
      }
    }
  }

  // The same pair can be rejected on every pass; report each place once.
  const seen = new Set<string>();
  const seenConflicts = new Set<string>();
  return {
    locations: current,
    keptApart: keptApart.filter(entry => {
      const key = `${entry.locationName}|${entry.reason}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    }),
    // A place folded from three stops reports the same field twice; say it once.
    contactConflicts: contactConflicts.filter(entry => {
      const key = `${entry.locationName}|${entry.field}|${entry.values.join('|')}`;
      if (seenConflicts.has(key)) return false;
      seenConflicts.add(key);
      return true;
    }),
  };
};
